<template>
  <div>
    <div class="wz-page-header">
      <h1 class="wz-page-title">Detalle de gratificación</h1>
    </div>

    <wz-card class="mb-4">
      <template #header>
        <div class="flex items-center justify-between w-full">
          <div class="wz-card-title">Resumen</div>
          <div class="flex gap-2">
            <wz-button variant="outline" @click="downloadXlsx">
              <i class="ri-download-2-line mr-1"></i>Exportar Excel
            </wz-button>
            <wz-button v-if="isBorrador" variant="primary" @click="confirmModal = true">
              <i class="ri-check-line mr-1"></i>Confirmar pago
            </wz-button>
          </div>
        </div>
      </template>

      <div class="flex flex-wrap -mx-2">
        <div class="w-full md:w-1/4 px-2 mb-2">
          <small class="text-muted block">Período</small>
          <strong>{{ periodLabel(result.periodType) }} {{ result.periodYear }}</strong>
        </div>
        <div class="w-full md:w-1/4 px-2 mb-2">
          <small class="text-muted block">Empresa</small>
          <strong>{{ result.companyName || '-' }}</strong>
        </div>
        <div class="w-full md:w-1/4 px-2 mb-2">
          <small class="text-muted block">Fecha de cálculo</small>
          <strong><span v-local-date="result.calculatedAt"></span></strong>
        </div>
        <div class="w-full md:w-1/4 px-2 mb-2">
          <small class="text-muted block">Estado</small>
          <wz-badge :variant="statusVariant(result.status)" pill>{{ statusLabel(result.status) }}</wz-badge>
        </div>
        <div class="w-full md:w-1/4 px-2 mb-2">
          <small class="text-muted block">Total colaboradores</small>
          <strong>{{ result.totalEmployees != null ? result.totalEmployees : '-' }}</strong>
        </div>
        <div class="w-full md:w-1/4 px-2 mb-2">
          <small class="text-muted block">Gratificación total</small>
          <strong><span v-decimal:pen="result.totalAmount"></span></strong>
        </div>
        <div class="w-full md:w-1/4 px-2 mb-2">
          <small class="text-muted block">Bonificación total</small>
          <strong><span v-decimal:pen="result.totalBonus"></span></strong>
        </div>
        <div class="w-full md:w-1/4 px-2 mb-2">
          <small class="text-muted block">Total a pagar</small>
          <strong><span v-decimal:pen="result.totalPayable"></span></strong>
        </div>
      </div>
    </wz-card>

    <wz-card title="Colaboradores">
      <wz-table>
        <thead>
          <tr>
            <th v-if="isBorrador" class="text-center">Incluir</th>
            <th>Colaborador</th>
            <th>DNI</th>
            <th>Fecha ingreso</th>
            <th class="text-center">Meses</th>
            <th class="text-right">Rem. computable</th>
            <th class="text-right">Gratificación</th>
            <th class="text-center">Tasa</th>
            <th class="text-right">Bonificación</th>
            <th class="text-right">Ajuste</th>
            <th class="text-right">Total</th>
            <th v-if="isBorrador" class="text-center">Opciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="d in result.details" :key="d.id" :class="{ 'row-excluded': d.isExcluded }">
            <td v-if="isBorrador" class="text-center">
              <input type="checkbox" :checked="!d.isExcluded" @change="toggleExclude(d)" />
            </td>
            <td>{{ d.employeeFullName }}</td>
            <td>{{ d.employeeDni }}</td>
            <td v-local-date="d.hireDate"></td>
            <td class="text-center">{{ d.completeMonths }}</td>
            <td class="text-right"><span v-decimal:pen="d.computableRemuneration"></span></td>
            <td class="text-right"><span v-decimal:pen="d.gratificationAmount"></span></td>
            <td class="text-center">{{ ratePct(d.bonusRate) }}</td>
            <td class="text-right"><span v-decimal:pen="d.bonusAmount"></span></td>
            <td class="text-right">
              <span v-if="d.manualAdjustment" v-decimal:pen="d.manualAdjustment"></span>
              <span v-else>-</span>
            </td>
            <td class="text-right"><span v-decimal:pen="d.totalPayable"></span></td>
            <td v-if="isBorrador" class="text-center">
              <wz-tooltip text="Ajuste manual">
                <wz-button variant="outline" size="sm" @click="openAdjust(d)">
                  <i class="ri-edit-line"></i>
                </wz-button>
              </wz-tooltip>
            </td>
          </tr>
          <tr v-if="!result.details || !result.details.length">
            <td :colspan="isBorrador ? 12 : 10" class="text-center text-muted">Sin colaboradores en este período.</td>
          </tr>
        </tbody>
      </wz-table>
    </wz-card>

    <!-- Modal: ajuste manual -->
    <wz-modal v-model="adjustModal" title="Ajuste manual">
      <div v-if="adjustTarget">
        <p class="mb-2"><strong>{{ adjustTarget.employeeFullName }}</strong></p>
        <wz-input label="Ajuste (S/)" type="number" step="0.01" v-model.number="adjustForm.adjustment"
          placeholder="Monto positivo o negativo" class="mb-3" />
        <wz-textarea label="Motivo" required v-model="adjustForm.reason" :rows="3"
          placeholder="Motivo del ajuste" :error="adjustReasonError" />
      </div>
      <template #footer>
        <wz-button variant="outline" @click="adjustModal = false">Cancelar</wz-button>
        <wz-button variant="primary" @click="saveAdjust">Guardar ajuste</wz-button>
      </template>
    </wz-modal>

    <!-- Modal: confirmar pago -->
    <wz-modal v-model="confirmModal" title="Confirmar pago">
      <p>
        ¿Confirmar la gratificación
        <strong>{{ periodLabel(result.periodType) }} {{ result.periodYear }}</strong>?
        Una vez confirmada no se podrá recalcular ni editar.
      </p>
      <template #footer>
        <wz-button variant="outline" @click="confirmModal = false">Cancelar</wz-button>
        <wz-button variant="primary" @click="doConfirm">Confirmar pago</wz-button>
      </template>
    </wz-modal>
  </div>
</template>

<script>
import { Alert } from '@wizepe/wizeui'
import { GratificationService } from '@/core/services'
import { Constants } from '@/core/utils'

export default {
  data() {
    return {
      result: { details: [] },
      adjustModal: false,
      adjustTarget: null,
      adjustForm: { adjustment: null, reason: '' },
      adjustReasonError: '',
      confirmModal: false,
    }
  },
  computed: {
    isBorrador() {
      return this.result.status === Constants.GratificationStatus.BORRADOR.key
    },
  },
  methods: {
    periodLabel(type) {
      const t = Constants.GratificationPeriodType[type]
      return t ? t.label.split(' —')[0] : type
    },
    statusLabel(status) {
      const s = Constants.GratificationStatus[status]
      return s ? s.label : status
    },
    statusVariant(status) {
      const s = Constants.GratificationStatus[status]
      return s ? s.color : 'dark'
    },
    ratePct(rate) {
      return rate != null ? `${(Number(rate) * 100).toFixed(2)}%` : '-'
    },
    async loadData() {
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await GratificationService.getPeriodDetail(this.$route.params.id)
        this.result = resp.payload || { details: [] }
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al cargar el período')
        this.$router.push({ name: 'gratification-list' })
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async toggleExclude(d) {
      const exclude = !d.isExcluded
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await GratificationService.excludeEmployee(d.id, exclude)
        this.result = resp.payload || this.result
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al actualizar la exclusión')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    openAdjust(d) {
      this.adjustTarget = d
      this.adjustForm = { adjustment: d.manualAdjustment || null, reason: d.adjustmentReason || '' }
      this.adjustReasonError = ''
      this.adjustModal = true
    },
    async saveAdjust() {
      this.adjustReasonError = ''
      if (!this.adjustForm.reason || !this.adjustForm.reason.trim()) {
        this.adjustReasonError = 'El motivo es obligatorio'
        return
      }
      const adj = this.adjustForm.adjustment != null ? Number(this.adjustForm.adjustment) : 0
      const newTotal = Number(this.adjustTarget.gratificationAmount || 0) + Number(this.adjustTarget.bonusAmount || 0) + adj
      if (newTotal < 0) {
        Alert.error('El ajuste dejaría el total en negativo. Corrija el monto.')
        return
      }
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await GratificationService.adjustAmount(this.adjustTarget.id, {
          adjustment: this.adjustForm.adjustment != null ? Number(this.adjustForm.adjustment) : 0,
          reason: this.adjustForm.reason,
        })
        this.result = resp.payload || this.result
        this.adjustModal = false
        Alert.success('Ajuste guardado')
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al guardar el ajuste')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async doConfirm() {
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await GratificationService.confirm(this.result.id)
        this.result = resp.payload || this.result
        this.confirmModal = false
        Alert.success('Período confirmado correctamente')
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al confirmar el período')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async downloadXlsx() {
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await GratificationService.downloadXlsx(this.result.id)
        const url = window.URL.createObjectURL(resp.data)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `gratificaciones-${(this.result.periodType || '').toLowerCase()}-${this.result.periodYear}.xlsx`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        console.error(e)
        Alert.error('Error al exportar el Excel')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
  },
  created() {
    this.loadData()
  },
}
</script>

<style scoped>
.row-excluded {
  opacity: 0.5;
}
</style>
